/**
 * Law OS v2 — Litigation Workspace (Phase 4)
 * Modular tab components for the single-screen Case workspace.
 * Strict light-mode palette (Indigo #2D3A8C, Emerald #10B981) per design_guidelines.json.
 */
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtDateTime, fmtDate } from "@/lib/time";
import {
    Clock, Briefcase, Gavel, FileText, Receipt, Users, MessageSquare,
    CheckSquare, StickyNote, Activity, Mail, Phone, MapPin, ArrowRight,
    Calendar, AlertTriangle, ExternalLink, Sparkles, Plus, Save,
    Scale, RefreshCw, Download, History, X, Loader2, Cpu
} from "lucide-react";
import { authDownload } from "@/lib/authDownload";

// ─────────── Shared helpers ───────────────────────────────────────────────────

const STATUS_TONE = {
    open:       { bg: "bg-blue-50",    text: "text-blue-700",    ring: "ring-blue-200" },
    on_hold:    { bg: "bg-amber-50",   text: "text-amber-700",   ring: "ring-amber-200" },
    closed:     { bg: "bg-slate-100",  text: "text-slate-600",   ring: "ring-slate-200" },
    won:        { bg: "bg-emerald-50", text: "text-emerald-700", ring: "ring-emerald-200" },
    lost:       { bg: "bg-rose-50",    text: "text-rose-700",    ring: "ring-rose-200" },
    settled:    { bg: "bg-violet-50",  text: "text-violet-700",  ring: "ring-violet-200" },
    active:     { bg: "bg-emerald-50", text: "text-emerald-700", ring: "ring-emerald-200" },
    disposed:   { bg: "bg-slate-100",  text: "text-slate-600",   ring: "ring-slate-200" },
};

export function CasePill({ label, tone }) {
    const t = STATUS_TONE[String(label || "").toLowerCase()] || { bg: "bg-slate-100", text: "text-slate-700", ring: "ring-slate-200" };
    return (
        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium ring-1 ${t.bg} ${t.text} ${t.ring}`}>
            {label || "—"}
        </span>
    );
}

export function SectionCard({ title, icon: Icon, action, children, testId }) {
    return (
        <div className="bg-card rounded-2xl border border-border shadow-lawos-sm p-6" data-testid={testId}>
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    {Icon && <Icon className="w-4 h-4 text-primary"/>}
                    <h3 className="font-heading text-sm font-semibold tracking-tight">{title}</h3>
                </div>
                {action}
            </div>
            {children}
        </div>
    );
}

function EmptyBlock({ icon: Icon, title, hint, action }) {
    return (
        <div className="text-center py-8">
            {Icon && <Icon className="w-8 h-8 text-muted-foreground/40 mx-auto mb-2"/>}
            <div className="text-sm font-medium text-foreground">{title}</div>
            {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
            {action && <div className="mt-3">{action}</div>}
        </div>
    );
}

// ─────────── 1. OVERVIEW ──────────────────────────────────────────────────────

export function OverviewTab({ caseDoc, client, assignedName, hearings, tasks, invoices }) {
    // "Next hearing" — single source of truth is `case.next_hearing_date_ecourts`
    // (the value refreshed by the eCourts sync + surfaced in Court History with
    // Orders). Falling back to the local hearings collection only if the case
    // has never been eCourts-synced, so manually-added hearings still surface
    // for non-eCourts cases.
    const ecourtsNext = caseDoc?.next_hearing_date_ecourts || null;
    const fallbackNext = hearings?.find(h => h.scheduled_at && new Date(h.scheduled_at) >= new Date());
    const nextHearingValue = ecourtsNext
        ? fmtDate(ecourtsNext)                        // eCourts field is date-only (no time)
        : (fallbackNext ? fmtDateTime(fallbackNext.scheduled_at) : "Not scheduled");
    const nextHearingTone = (ecourtsNext || fallbackNext) ? "indigo" : "muted";
    const openTasks = tasks?.filter(t => t.status !== "completed").length || 0;
    const unpaid = invoices?.filter(i => i.status !== "paid").reduce((s, i) => s + (i.amount - (i.paid_amount || 0)), 0) || 0;

    return (
        <div className="grid lg:grid-cols-3 gap-5" data-testid="case-overview-tab">
            {/* Left — case meta */}
            <div className="lg:col-span-2 space-y-5">
                <SectionCard title="Litigation details" icon={Briefcase} testId="case-overview-details">
                    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                        <MetaRow k="Original case #" v={caseDoc.original_suit_number} mono accent/>
                        <MetaRow k="Court case #" v={caseDoc.court_case_number} mono/>
                        <MetaRow k="Internal #" v={caseDoc.internal_number} mono/>
                        <MetaRow k="Court" v={caseDoc.court}/>
                        <MetaRow k="Bench" v={caseDoc.bench}/>
                        <MetaRow k="Hearing type" v={caseDoc.hearing_type}/>
                        <MetaRow k="Category" v={caseDoc.category}/>
                        <MetaRow k="Type" v={caseDoc.type}/>
                        <MetaRow k="Stage" v={caseDoc.stage}/>
                        <MetaRow k="Priority" v={caseDoc.priority}/>
                        <MetaRow k="Assigned to" v={assignedName}/>
                        <MetaRow k="CNR" v={caseDoc.cnr} mono/>
                    </div>
                </SectionCard>

                {caseDoc.description && (
                    <SectionCard title="Notes & description" icon={StickyNote} testId="case-overview-notes">
                        <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">{caseDoc.description}</p>
                    </SectionCard>
                )}
            </div>

            {/* Right — sidebar cards */}
            <div className="space-y-5">
                <ClientCard client={client}/>
                <SectionCard title="At a glance" icon={Activity} testId="case-overview-glance">
                    <div className="space-y-3">
                        {/* Next hearing — shows auto-sync indicator when data was updated automatically */}
                        <div className="flex items-start justify-between gap-2">
                            <span className="flex items-center gap-2 text-xs text-muted-foreground shrink-0">
                                <Gavel className="w-3.5 h-3.5"/> Next hearing
                            </span>
                            <div className="flex flex-col items-end gap-0.5">
                                <span className={`text-sm font-medium ${nextHearingTone === "indigo" ? "text-primary" : "text-muted-foreground"}`}>
                                    {nextHearingValue}
                                </span>
                                {caseDoc?.sync_source === "external_scraper" && caseDoc?.next_hearing_date_ecourts && (
                                    <span className="inline-flex items-center gap-1 text-[10px] text-violet-500 dark:text-violet-400" title={`Last auto-synced: ${caseDoc.external_synced_at}`}>
                                        <Cpu className="w-3 h-3"/> auto-synced
                                    </span>
                                )}
                            </div>
                        </div>
                        <GlanceRow icon={CheckSquare} label="Open tasks" value={openTasks} tone={openTasks > 0 ? "warning" : "muted"}/>
                        <GlanceRow icon={Receipt} label="Outstanding ₹" value={`₹${Number(unpaid).toLocaleString("en-IN")}`} tone={unpaid > 0 ? "warning" : "emerald"}/>
                        <GlanceRow icon={FileText} label="Hearings logged" value={hearings?.length || 0} tone="muted"/>
                    </div>
                </SectionCard>
            </div>
        </div>
    );
}

function MetaRow({ k, v, mono, accent }) {
    return (
        <div className="flex items-baseline justify-between gap-3 border-b border-border/60 pb-2 last:border-0">
            <span className="text-xs text-muted-foreground">{k}</span>
            <span className={`text-sm text-right ${mono ? "font-mono" : ""} ${accent ? "text-primary font-medium" : "text-foreground"}`}>{v || "—"}</span>
        </div>
    );
}

function GlanceRow({ icon: Icon, label, value, tone }) {
    const toneCls = { indigo: "text-primary", warning: "text-amber-600", emerald: "text-emerald-600", muted: "text-muted-foreground" }[tone] || "text-foreground";
    return (
        <div className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-xs text-muted-foreground"><Icon className="w-3.5 h-3.5"/> {label}</span>
            <span className={`text-sm font-medium ${toneCls}`}>{value}</span>
        </div>
    );
}

function ClientCard({ client }) {
    if (!client) return (
        <SectionCard title="Client" icon={Users} testId="case-overview-client">
            <p className="text-xs text-muted-foreground">No client linked.</p>
        </SectionCard>
    );
    return (
        <SectionCard title="Client" icon={Users} testId="case-overview-client"
            action={<Link to={`/app/clients/${client.id}`} className="text-xs text-primary hover:underline inline-flex items-center gap-1">View <ArrowRight className="w-3 h-3"/></Link>}>
            <div className="space-y-2.5 text-sm">
                <div className="font-medium text-foreground">{client.name}</div>
                <div className="text-xs text-muted-foreground font-mono">{client.code}</div>
                {client.mobile && <div className="flex items-center gap-2 text-xs"><Phone className="w-3 h-3 text-muted-foreground"/> <span className="text-foreground">{client.mobile}</span></div>}
                {client.email && <div className="flex items-center gap-2 text-xs"><Mail className="w-3 h-3 text-muted-foreground"/> <span className="text-foreground truncate">{client.email}</span></div>}
                {client.city && <div className="flex items-center gap-2 text-xs"><MapPin className="w-3 h-3 text-muted-foreground"/> <span className="text-foreground">{client.city}</span></div>}
            </div>
        </SectionCard>
    );
}

// ─────────── 2. TIMELINE ──────────────────────────────────────────────────────

/**
 * CourtHistoryTab — renders the eCourts-style "Case History with Orders"
 * timeline. Reads from `/api/case/{id}/court-history` which combines
 * historical hearings (db.case_hearings), interim + judgment orders
 * (db.case_orders) and the upcoming next-hearing date (case doc) into a
 * single newest-first stream. PDF downloads go through `authDownload` so the
 * JWT is sent in the Authorization header.
 */
export function CourtHistoryTab({ caseId, cnr, onSynced }) {
    const [data, setData] = useState(null);
    const [syncing, setSyncing] = useState(false);
    const [error, setError] = useState(null);
    const [syncDiff, setSyncDiff] = useState(null);   // { fieldLabel: { prev, new } }

    const load = () => {
        api.get(`/ecourts/case/${caseId}/court-history`)
            .then(r => { setData(r.data); setError(null); })
            .catch(e => setError(apiError(e)));
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [caseId]);

    const refreshFromEcourts = async () => {
        if (!cnr) { toast.error("Case has no CNR — cannot sync"); return; }
        // Warn user that this is a paid call if history already exists
        const existingCount = (data?.entries || []).filter(e => e.kind !== "next_hearing").length;
        if (existingCount > 0) {
            const confirmed = window.confirm(
                `Court History already has ${existingCount} entr${existingCount === 1 ? "y" : "ies"} saved locally.\n\n` +
                `"Refresh from eCourts" makes a fresh API call and will charge your wallet again.\n\n` +
                `Proceed only if you need the very latest data (new orders, updated hearing dates).`
            );
            if (!confirmed) return;
        }
        setSyncing(true);
        try {
            const { data } = await api.post(`/ecourts/case/${cnr}/sync-to/${caseId}`);
            const bits = [];
            if (data.updated_fields?.length) bits.push(`${data.updated_fields.length} field${data.updated_fields.length === 1 ? "" : "s"} updated`);
            if (data.hearings_saved) bits.push(`${data.hearings_saved} hearing${data.hearings_saved === 1 ? "" : "s"}`);
            if (data.orders_saved) bits.push(`${data.orders_saved} order${data.orders_saved === 1 ? "" : "s"}`);
            toast.success(bits.length ? `Re-synced from eCourts · ${bits.join(" · ")}` : "Re-synced from eCourts");
            // Show diff popup if backend returned before/after values
            if (data.changes && Object.keys(data.changes).length > 0) {
                setSyncDiff({ changes: data.changes, hearings: data.hearings_saved, orders: data.orders_saved });
            }
            load();
            if (typeof onSynced === "function") onSynced();
        } catch (e) { toast.error(apiError(e)); }
        finally { setSyncing(false); }
    };

    if (error) return <SectionCard title="Court History" icon={History} testId="case-court-history-tab">
        <div className="text-sm text-rose-600">{error}</div>
    </SectionCard>;
    if (!data) return <SectionCard title="Court History" icon={History} testId="case-court-history-tab">
        <div className="text-sm text-muted-foreground">Loading court history…</div>
    </SectionCard>;

    const groups = groupByDate(data.entries);

    return (
        <>
        {/* ── Sync diff popup ────────────────────────────────────────────── */}
        {syncDiff && (
            <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4"
                 onClick={() => setSyncDiff(null)}>
                <div className="bg-background border border-border w-full max-w-lg flex flex-col max-h-[85vh]"
                     onClick={e => e.stopPropagation()} data-testid="sync-diff-modal">
                    {/* Header */}
                    <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
                        <div>
                            <div className="text-sm font-medium">Re-synced from eCourts</div>
                            <div className="text-[10px] text-muted-foreground mt-0.5">
                                {Object.keys(syncDiff.changes).length} field{Object.keys(syncDiff.changes).length === 1 ? "" : "s"} updated
                                {syncDiff.hearings > 0 && ` · ${syncDiff.hearings} hearing${syncDiff.hearings === 1 ? "" : "s"}`}
                                {syncDiff.orders > 0 && ` · ${syncDiff.orders} order${syncDiff.orders === 1 ? "" : "s"}`}
                            </div>
                        </div>
                        <button onClick={() => setSyncDiff(null)}
                            data-testid="sync-diff-close"
                            className="text-muted-foreground hover:text-foreground text-2xl leading-none p-1">×</button>
                    </div>
                    {/* Changes list */}
                    <div className="overflow-y-auto p-4 space-y-2">
                        {Object.entries(syncDiff.changes).map(([label, diff]) => (
                            <div key={label} className="border border-border bg-muted/30 p-3 text-xs">
                                <div className="font-medium text-foreground mb-2 uppercase tracking-[0.12em] text-[10px]">{label}</div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <div className="text-[9px] uppercase tracking-[0.14em] text-muted-foreground mb-1">Before</div>
                                        <div className="text-rose-400 font-mono break-words leading-relaxed">{diff.prev || "—"}</div>
                                    </div>
                                    <div>
                                        <div className="text-[9px] uppercase tracking-[0.14em] text-muted-foreground mb-1">After</div>
                                        <div className="text-emerald-400 font-mono break-words leading-relaxed">{diff.new || "—"}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                        {syncDiff.hearings > 0 && (
                            <div className="border border-border bg-muted/30 p-3 text-xs flex items-center justify-between">
                                <span className="text-muted-foreground uppercase tracking-[0.12em] text-[10px]">Hearings synced</span>
                                <span className="text-emerald-400 font-mono">{syncDiff.hearings} new</span>
                            </div>
                        )}
                        {syncDiff.orders > 0 && (
                            <div className="border border-border bg-muted/30 p-3 text-xs flex items-center justify-between">
                                <span className="text-muted-foreground uppercase tracking-[0.12em] text-[10px]">Orders synced</span>
                                <span className="text-emerald-400 font-mono">{syncDiff.orders} new</span>
                            </div>
                        )}
                    </div>
                    <div className="px-4 pb-4 shrink-0">
                        <button onClick={() => setSyncDiff(null)} data-testid="sync-diff-close-btn"
                            className="w-full border border-border text-xs py-2 uppercase tracking-[0.16em] hover:bg-muted text-muted-foreground">
                            Close
                        </button>
                    </div>
                </div>
            </div>
        )}
        <SectionCard title="Case History with Orders" icon={History} testId="case-court-history-tab"
            action={cnr ? (
                <button onClick={refreshFromEcourts} disabled={syncing} data-testid="court-history-refresh-btn"
                    className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline disabled:opacity-50">
                    <RefreshCw className={`w-3 h-3 ${syncing ? "animate-spin" : ""}`}/>
                    {syncing ? "Syncing…" : "Refresh from eCourts"}
                </button>
            ) : null}>

            {/* Final-Status banner */}
            <div className={`mb-4 px-4 py-3 border-l-4 ${data.final_status.is_pending ? "border-amber-400 bg-amber-50" : "border-slate-400 bg-slate-50"}`}
                 data-testid="court-history-final-status">
                <div className="flex items-center gap-2 text-sm font-semibold">
                    <Gavel className={`w-4 h-4 ${data.final_status.is_pending ? "text-amber-600" : "text-slate-600"}`}/>
                    <span className={data.final_status.is_pending ? "text-amber-700" : "text-slate-700"}>
                        {data.final_status.label}
                    </span>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">{data.final_status.sublabel}</div>
            </div>

            {data.entries.length === 0 ? (
                <EmptyBlock icon={History} title="No history yet" hint="Court History rows appear after the first eCourts sync."/>
            ) : (
                <ol className="relative border-l border-indigo-200 ml-2 space-y-6">
                    {groups.map(([dateIso, items], gi) => (
                        <li key={dateIso} className="ml-5" data-testid={`court-history-group-${gi}`}>
                            <span className="absolute -left-3 mt-1 w-6 h-6 rounded-full bg-indigo-50 border-2 border-indigo-300 flex items-center justify-center">
                                <Clock className="w-3 h-3 text-indigo-600"/>
                            </span>
                            <div className="text-sm font-semibold text-indigo-700 mb-2">{fmtDate(dateIso)}</div>
                            <div className="space-y-2">
                                {items.map((e, i) => <CourtHistoryCard key={i} entry={e} caseId={caseId}/>)}
                            </div>
                        </li>
                    ))}
                </ol>
            )}
        </SectionCard>
        </>
    );
}

function groupByDate(entries) {
    const map = new Map();
    for (const e of entries) {
        const key = (e.date || "").slice(0, 10);
        if (!key) continue;
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(e);
    }
    return Array.from(map.entries());
}

function CourtHistoryCard({ entry, caseId }) {
    const isUpcoming = entry.kind === "next_hearing";
    const isOrder = entry.kind === "order" || entry.kind === "judgment";
    const title = isOrder
        ? (entry.description || (entry.kind === "judgment" ? "Judgment Order" : "Interim Order"))
        : (entry.purpose || "Hearing");
    return (
        <div className={`border-l-4 ${isUpcoming ? "border-amber-400" : "border-indigo-300"} bg-white rounded-r-md shadow-sm px-3 py-2.5 hover:shadow-md transition-shadow`}
             data-testid="court-history-card">
            <div className="flex items-start gap-2">
                <FileText className="w-3.5 h-3.5 text-indigo-500 mt-0.5"/>
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-foreground">{title}</div>
                    {isUpcoming && <div className="text-xs text-amber-600 mt-0.5">{entry.subtitle || "Upcoming hearing date"}</div>}
                    {entry.judge && (
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1">
                            <Gavel className="w-3 h-3"/> {entry.judge}
                        </div>
                    )}
                    {isOrder && entry.filename && (
                        <button type="button"
                            onClick={() => authDownload(
                                `/api/ecourts/case/${caseId}/orders/${encodeURIComponent(entry.filename)}/download`,
                                entry.filename.split("/").pop() || "order.pdf"
                            )}
                            data-testid={`court-history-order-download-${entry.order_id}`}
                            className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-1">
                            <Download className="w-3 h-3"/> View PDF
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

// ─────────── 2. TIMELINE ──────────────────────────────────────────────────────

export function TimelineTab({ caseId }) {
    const [events, setEvents] = useState(null);
    useEffect(() => {
        api.get(`/audit/entity/case/${caseId}`)
            .then(r => setEvents(r.data || []))
            .catch(() => setEvents([]));
    }, [caseId]);
    if (events === null) return <div className="text-sm text-muted-foreground">Loading timeline…</div>;
    if (events.length === 0) return (
        <SectionCard title="Activity timeline" icon={Clock} testId="case-timeline-tab">
            <EmptyBlock icon={Clock} title="No timeline yet" hint="Activity will appear here as the litigation progresses."/>
        </SectionCard>
    );
    return (
        <SectionCard title="Activity timeline" icon={Clock} testId="case-timeline-tab">
            <ol className="relative border-l border-border ml-2 space-y-5">
                {events.slice(0, 100).map((e, i) => (
                    <li key={e.id || i} className="ml-5">
                        <span className="absolute -left-1.5 mt-1.5 w-3 h-3 rounded-full bg-primary ring-4 ring-card"/>
                        <div className="text-xs text-muted-foreground">{fmtDateTime(e.timestamp || e.created_at)}</div>
                        <div className="text-sm text-foreground font-medium">{(e.action || e.event || "").replace(/_/g, " ")}</div>
                        {e.user_name && <div className="text-xs text-muted-foreground mt-0.5">by {e.user_name}</div>}
                        {e.reason && <div className="text-xs text-muted-foreground mt-1 italic">&ldquo;{e.reason}&rdquo;</div>}
                    </li>
                ))}
            </ol>
        </SectionCard>
    );
}

// ─────────── 3. HEARINGS ──────────────────────────────────────────────────────

export function HearingsTab({ hearings }) {
    if (!hearings?.length) return (
        <SectionCard title="Hearings" icon={Gavel} testId="case-hearings-tab">
            <EmptyBlock icon={Gavel} title="No hearings yet" hint="Scheduled hearings linked to this litigation will appear here."/>
        </SectionCard>
    );
    return (
        <SectionCard title="Hearings" icon={Gavel} testId="case-hearings-tab"
            action={<Link to={`/app/hearings?case_id=${hearings[0]?.case_id}`} className="text-xs text-primary hover:underline">View all</Link>}>
            <div className="divide-y divide-border">
                {hearings.map(h => (
                    <div key={h.id} className="py-3 flex items-start justify-between gap-4">
                        <div className="min-w-0">
                            <div className="text-sm font-medium text-foreground">{h.stage || "Hearing"}</div>
                            <div className="text-xs text-muted-foreground mt-0.5">{fmtDateTime(h.scheduled_at)}</div>
                            {h.notes && <div className="text-xs text-foreground mt-1 line-clamp-2">{h.notes}</div>}
                        </div>
                        <CasePill label={h.status || "scheduled"} tone="indigo"/>
                    </div>
                ))}
            </div>
        </SectionCard>
    );
}

// ─────────── 4. TASKS ─────────────────────────────────────────────────────────

export function TasksTab({ caseId }) {
    const [tasks, setTasks] = useState(null);
    useEffect(() => {
        api.get(`/tasks`, { params: { case_id: caseId } })
            .then(r => setTasks(r.data || []))
            .catch(() => setTasks([]));
    }, [caseId]);
    if (tasks === null) return <div className="text-sm text-muted-foreground">Loading tasks…</div>;
    return (
        <SectionCard title="Tasks" icon={CheckSquare} testId="case-tasks-tab"
            action={
                <Link to={`/app/tasks?case_id=${caseId}&new=1`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:opacity-90">
                    <Plus className="w-3 h-3"/> New task
                </Link>
            }>
            {tasks.length === 0 ? (
                <EmptyBlock icon={CheckSquare} title="No tasks yet" hint="Break this litigation down into actionable steps."/>
            ) : (
                <div className="divide-y divide-border">
                    {tasks.map(t => (
                        <div key={t.id} className="py-3 flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <div className="text-sm font-medium text-foreground truncate">{t.title}</div>
                                <div className="text-xs text-muted-foreground mt-0.5 flex gap-3">
                                    {t.due_date && <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3"/> {fmtDate(t.due_date)}</span>}
                                    <span>Priority: {t.priority || "—"}</span>
                                </div>
                            </div>
                            <CasePill label={t.status || "pending"}/>
                        </div>
                    ))}
                </div>
            )}
        </SectionCard>
    );
}

// ─────────── 5. NOTES ─────────────────────────────────────────────────────────
// Notes use the litigation `description` field — single source of truth for unstructured case notes.

export function NotesTab({ caseDoc, onSaved }) {
    const [text, setText] = useState(caseDoc.description || "");
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);
    const dirty = text !== (caseDoc.description || "");

    const save = async () => {
        if (!reason.trim() || reason.trim().length < 3) {
            toast.error("Add a short reason (min 3 chars) for the audit trail");
            return;
        }
        setSaving(true);
        try {
            await api.patch(`/cases/${caseDoc.id}`, { description: text },
                { headers: { "X-Change-Reason": reason.trim() } });
            toast.success("Notes saved · logged to audit trail");
            setReason("");
            onSaved?.();
        } catch (e) { toast.error(apiError(e)); }
        finally { setSaving(false); }
    };

    return (
        <SectionCard title="Notes" icon={StickyNote} testId="case-notes-tab">
            <textarea
                value={text}
                onChange={e => setText(e.target.value)}
                placeholder="Add internal notes — context, strategy, follow-ups…"
                rows={10}
                data-testid="case-notes-textarea"
                className="w-full bg-background border border-border rounded-lg px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/60 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none resize-y"
            />
            {dirty && (
                <div className="mt-3 space-y-3">
                    <input
                        value={reason}
                        onChange={e => setReason(e.target.value)}
                        placeholder="Why this edit? (logged to audit trail)"
                        data-testid="case-notes-reason"
                        className="w-full bg-background border border-border rounded-lg px-4 py-2 text-sm text-foreground focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                    />
                    <div className="flex justify-end">
                        <button onClick={save} disabled={saving} data-testid="case-notes-save"
                            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 disabled:opacity-50">
                            <Save className="w-3.5 h-3.5"/> {saving ? "Saving…" : "Save notes"}
                        </button>
                    </div>
                </div>
            )}
        </SectionCard>
    );
}

// ─────────── 6. DOCUMENTS ─────────────────────────────────────────────────────

export function DocumentsTab({ docs, caseId }) {
    const [previewDoc, setPreviewDoc] = useState(null);

    return (
        <>
            <SectionCard title="Documents" icon={FileText} testId="case-documents-tab"
                action={
                    <Link to={`/app/documents?case_id=${caseId}&new=1`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:opacity-90">
                        <Plus className="w-3 h-3"/> Upload
                    </Link>
                }>
                {!docs?.length ? (
                    <EmptyBlock icon={FileText} title="No documents yet" hint="Upload pleadings, evidence and orders to keep them at hand."/>
                ) : (
                    <div className="grid sm:grid-cols-2 gap-3">
                        {docs.map(d => {
                            const isHeadnote = d.kind === "ai_headnote";
                            const card = (
                                <div className="w-full text-left group flex items-start gap-3 p-3 rounded-xl border border-border hover:border-primary/50 hover:bg-muted/50 transition-colors"
                                    data-testid={`case-doc-${d.id}`}>
                                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${isHeadnote ? "bg-primary/10" : "bg-primary/10"}`}>
                                        {isHeadnote ? <Sparkles className="w-4 h-4 text-primary"/> : <FileText className="w-4 h-4 text-primary"/>}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm font-medium text-foreground truncate">
                                            {d.display_name || d.filename || d.title || d.kind}
                                        </div>
                                        <div className="text-xs text-muted-foreground mt-0.5 flex gap-2">
                                            <span>{isHeadnote ? "AI Headnote" : d.kind}</span>
                                            <span>·</span>
                                            <span>{fmtDate(d.created_at)}</span>
                                        </div>
                                    </div>
                                    <CasePill label={d.status || "ready"}/>
                                </div>
                            );
                            // AI headnotes open an in-page preview (no separate
                            // detail route — saves a navigation hop and lets
                            // users keep their litigation context).
                            return isHeadnote ? (
                                <button key={d.id} type="button"
                                    onClick={() => setPreviewDoc(d)}
                                    data-testid={`case-doc-open-${d.id}`}
                                    className="text-left">{card}</button>
                            ) : (
                                <Link to={`/app/documents?case_id=${caseId}&doc_id=${d.id}`} key={d.id} className="block">{card}</Link>
                            );
                        })}
                    </div>
                )}
            </SectionCard>

            {previewDoc && (
                <HeadnoteDocumentPreview doc={previewDoc} onClose={() => setPreviewDoc(null)}/>
            )}
        </>
    );
}


/**
 * In-page preview for a saved AI headnote document. Renders the full
 * IRAC structure (Summary / Issue / Rule / Application / Conclusion +
 * Citations + Keywords) and exposes a one-click *Original PDF* download
 * via the existing /api/legal-search/order-pdf-by-docid endpoint. Stays
 * inside the litigation context so the user never loses their tab state.
 */
function HeadnoteDocumentPreview({ doc, onClose }) {
    const h = doc.headnote || {};
    const [downloading, setDownloading] = useState(false);
    const [resolvedDocid, setResolvedDocid] = useState(doc.docid || null);

    // For documents attached BEFORE the docid-persistence fix, the row
    // won't carry docid — fall back to looking it up from the source
    // headnote row so the Download Original PDF button still works.
    useEffect(() => {
        if (resolvedDocid || !doc.source_headnote_id) return;
        let cancelled = false;
        api.get(`/legal-search/headnotes/${doc.source_headnote_id}`)
            .then(r => { if (!cancelled) setResolvedDocid(r.data?.docid || r.data?.ik_docid || null); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [resolvedDocid, doc.source_headnote_id]);

    const downloadOriginal = async () => {
        const docid = resolvedDocid;
        if (!docid && !doc.cnr) {
            toast.error("Original PDF metadata not available for this headnote");
            return;
        }
        setDownloading(true);
        const base = process.env.REACT_APP_BACKEND_URL || "";
        const url = docid
            ? `${base}/api/indiankanoon/origdoc/${docid}/download`
            : `${base}/api/ecourts/cases/${encodeURIComponent(doc.cnr)}/order-pdf`;
        const filename = docid
            ? `court-order-${docid}.pdf`
            : `order-${doc.cnr}.pdf`;
        try {
            const resp = await fetch(url, { credentials: "include" });
            if (!resp.ok) throw new Error(await resp.text() || `HTTP ${resp.status}`);
            const blob = await resp.blob();
            const blobUrl = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = blobUrl; a.download = filename;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(blobUrl);
            toast.success("Original PDF downloaded");
        } catch (e) {
            toast.error(apiError(e, "Could not download the original PDF"));
        } finally { setDownloading(false); }
    };

    // The button is ALWAYS rendered — we want the user to discover the
    // capability — but it disables itself + shows a helpful tooltip when
    // we genuinely have no upstream id to fall back to.
    const canDownload = Boolean(resolvedDocid || doc.cnr);

    return (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4 overflow-y-auto"
            onClick={onClose}
            data-testid="case-headnote-preview">
            <div className="bg-background rounded-t-2xl sm:rounded-2xl border border-border w-full max-w-3xl sm:my-8 max-h-[92vh] sm:max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}>
                <div className="sticky top-0 bg-background border-b border-border px-4 sm:px-5 py-3 flex items-start justify-between gap-3 z-10">
                    <div className="min-w-0 flex-1">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-primary font-semibold flex items-center gap-1.5">
                            <Sparkles className="w-3 h-3"/> AI Legal Headnote
                        </div>
                        <h2 className="font-heading text-base sm:text-lg font-bold text-foreground mt-0.5 break-words leading-tight">
                            {doc.case_title || doc.display_name || doc.title}
                        </h2>
                        <div className="text-[11px] text-muted-foreground mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
                            {doc.court_name && <span>{doc.court_name}</span>}
                            {doc.order_date && <><span>·</span><span>{doc.order_date}</span></>}
                            {doc.cnr && <><span>·</span><span className="font-mono">CNR {doc.cnr}</span></>}
                            {resolvedDocid && <><span>·</span><span className="font-mono">IK {resolvedDocid}</span></>}
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1.5 hover:bg-muted rounded-md shrink-0" data-testid="case-headnote-preview-close">
                        <X className="w-4 h-4"/>
                    </button>
                </div>

                <div className="p-4 sm:p-5 space-y-4 text-sm">
                    <button onClick={downloadOriginal} disabled={downloading || !canDownload}
                        title={canDownload ? "Download the original court order PDF" : "Original PDF source not available for this headnote"}
                        data-testid="case-headnote-original-pdf"
                        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl border border-border hover:border-primary hover:bg-primary/5 transition disabled:opacity-50 disabled:cursor-not-allowed">
                        {downloading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Download className="w-4 h-4"/>}
                        <span className="font-medium">Download Original PDF</span>
                    </button>

                    {h.summary && (
                        <Section label="Summary" body={h.summary}/>
                    )}
                    {h.issue && (
                        <Section label="Issue" body={h.issue}/>
                    )}
                    {h.rule && (
                        <Section label="Rule" body={h.rule}/>
                    )}
                    {h.application && (
                        <Section label="Application" body={h.application}/>
                    )}
                    {h.conclusion && (
                        <Section label="Conclusion" body={h.conclusion}/>
                    )}
                    {Array.isArray(h.citations) && h.citations.length > 0 && (
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-primary font-semibold mb-1.5">Citations</div>
                            <ul className="list-disc pl-5 space-y-0.5">
                                {h.citations.map((c, i) => <li key={i} className="text-foreground">{c}</li>)}
                            </ul>
                        </div>
                    )}
                    {Array.isArray(h.keywords) && h.keywords.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 pt-1">
                            {h.keywords.map((k, i) => (
                                <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary">#{k}</span>
                            ))}
                        </div>
                    )}
                    {h.generated_at && (
                        <div className="text-[10px] text-muted-foreground pt-2 border-t border-border">
                            Generated by {h.model || "AI"} · {fmtDateTime(h.generated_at)}
                        </div>
                    )}

                    {!h.summary && !h.issue && doc.content && (
                        // Backwards-compat: older headnote docs only have the markdown
                        // content blob — render it as a pre-formatted block.
                        <pre className="whitespace-pre-wrap break-words font-sans text-sm text-foreground bg-muted/30 p-3 rounded-xl border border-border">
                            {doc.content}
                        </pre>
                    )}
                </div>
            </div>
        </div>
    );
}

const Section = ({ label, body }) => (
    <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-primary font-semibold mb-1">{label}</div>
        <p className="text-foreground leading-relaxed whitespace-pre-wrap break-words">{body}</p>
    </div>
);

// ─────────── 7. COMMUNICATIONS ────────────────────────────────────────────────

export function CommunicationsTab({ caseId }) {
    const [comms, setComms] = useState(null);
    const [refreshing, setRefreshing] = useState(false);

    const load = useCallback(() => {
        api.get(`/communications`, { params: { case_id: caseId } })
            .then(r => setComms(r.data || []))
            .catch(() => setComms([]));
    }, [caseId]);

    // Fetch on mount + every 30 s while the tab is active
    useEffect(() => {
        load();
        const interval = setInterval(load, 30_000);
        return () => clearInterval(interval);
    }, [load]);

    const manualRefresh = async () => {
        setRefreshing(true);
        await api.get(`/communications`, { params: { case_id: caseId } })
            .then(r => setComms(r.data || []))
            .catch(() => {});
        setRefreshing(false);
    };

    if (comms === null) return <div className="text-sm text-muted-foreground">Loading communications…</div>;
    return (
        <SectionCard title="Communications" icon={MessageSquare} testId="case-communications-tab"
            action={
                <div className="flex items-center gap-2">
                    <button onClick={manualRefresh} disabled={refreshing}
                        data-testid="comms-refresh-btn"
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-border text-xs text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors disabled:opacity-40">
                        <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`}/>
                        <span className="hidden sm:inline">Refresh</span>
                    </button>
                    <Link to={`/app/communications?case_id=${caseId}&new=1`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:opacity-90">
                        <Plus className="w-3 h-3"/> Compose
                    </Link>
                </div>
            }>
            {comms.length === 0 ? (
                <EmptyBlock icon={MessageSquare} title="No messages yet" hint="Emails and WhatsApp sent for this litigation will appear here."/>
            ) : (
                <div className="divide-y divide-border">
                    {comms.slice(0, 50).map(c => (
                        <div key={c.id} className="py-3 flex items-start justify-between gap-4" data-testid={`case-comm-${c.id}`}>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 mb-0.5">
                                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{c.channel || "email"}</span>
                                    {c.sent_at && <span className="text-xs text-muted-foreground">· {fmtDateTime(c.sent_at)}</span>}
                                </div>
                                <div className="text-sm font-medium text-foreground truncate">{c.subject || c.body?.slice(0, 60) || "(no subject)"}</div>
                                <div className="text-xs text-muted-foreground mt-0.5">To: {c.to || c.recipient || "—"}</div>
                            </div>
                            <CasePill label={c.status || "sent"}/>
                        </div>
                    ))}
                </div>
            )}
        </SectionCard>
    );
}

// ─────────── 8. BILLING ───────────────────────────────────────────────────────

export function BillingTab({ invoices, caseId }) {
    const total = invoices?.reduce((s, i) => s + (i.amount || 0), 0) || 0;
    const paid = invoices?.reduce((s, i) => s + (i.paid_amount || 0), 0) || 0;
    const outstanding = total - paid;

    return (
        <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="case-billing-summary">
                <SummaryStat label="Invoiced" value={total} tone="indigo"/>
                <SummaryStat label="Collected" value={paid} tone="emerald"/>
                <SummaryStat label="Outstanding" value={outstanding} tone={outstanding > 0 ? "warning" : "muted"}/>
            </div>
            <SectionCard title="Invoices" icon={Receipt} testId="case-billing-tab"
                action={
                    <Link to={`/app/invoices?case_id=${caseId}&new=1`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:opacity-90">
                        <Plus className="w-3 h-3"/> New invoice
                    </Link>
                }>
                {!invoices?.length ? (
                    <EmptyBlock icon={Receipt} title="No invoices yet" hint="Track time and bill the client from here."/>
                ) : (
                    <div className="divide-y divide-border">
                        {invoices.map(inv => (
                            <Link to={`/app/invoices/${inv.id}`} key={inv.id}
                                className="py-3 flex items-center justify-between gap-3 hover:bg-muted/40 -mx-2 px-2 rounded-lg transition-colors"
                                data-testid={`case-invoice-${inv.id}`}>
                                <div>
                                    <div className="text-sm font-medium text-foreground font-mono">{inv.number}</div>
                                    <div className="text-xs text-muted-foreground mt-0.5">{fmtDate(inv.created_at)}</div>
                                </div>
                                <div className="text-right">
                                    <div className="text-sm font-semibold text-foreground">₹{Number(inv.amount || 0).toLocaleString("en-IN")}</div>
                                    <div className="text-xs text-muted-foreground mt-0.5">Paid ₹{Number(inv.paid_amount || 0).toLocaleString("en-IN")}</div>
                                </div>
                                <CasePill label={inv.status || "unpaid"}/>
                            </Link>
                        ))}
                    </div>
                )}
            </SectionCard>
        </div>
    );
}

function SummaryStat({ label, value, tone }) {
    const toneCls = { indigo: "text-primary", emerald: "text-emerald-600", warning: "text-amber-600", muted: "text-muted-foreground" }[tone] || "text-foreground";
    return (
        <div className="bg-card rounded-2xl border border-border p-5 shadow-lawos-sm">
            <div className="text-xs text-muted-foreground">{label}</div>
            <div className={`text-2xl font-bold mt-1 ${toneCls}`}>₹{Number(value || 0).toLocaleString("en-IN")}</div>
        </div>
    );
}

// ─────────── 9. TEAM ──────────────────────────────────────────────────────────

export function TeamTab({ caseDoc, staff, onReassign }) {
    const assignee = staff?.find(s => s.id === caseDoc.assigned_to);
    return (
        <SectionCard title="Team" icon={Users} testId="case-team-tab"
            action={
                <button onClick={onReassign} data-testid="case-team-reassign-btn"
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:opacity-90">
                    <ArrowRight className="w-3 h-3"/> Reassign
                </button>
            }>
            {assignee ? (
                <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/40 border border-border">
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-medium">
                        {assignee.name?.[0]?.toUpperCase() || "U"}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-foreground">{assignee.name}</div>
                        <div className="text-xs text-muted-foreground">{assignee.role?.replace(/_/g, " ")} · {assignee.email}</div>
                    </div>
                    <CasePill label="Lead"/>
                </div>
            ) : (
                <EmptyBlock icon={Users} title="No one assigned yet" hint="Reassign this litigation to start tracking ownership."/>
            )}

            {staff?.length > 1 && (
                <div className="mt-5">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2">Other team members</div>
                    <div className="flex flex-wrap gap-2">
                        {staff.filter(s => s.id !== caseDoc.assigned_to).slice(0, 12).map(s => (
                            <div key={s.id} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-muted text-xs text-foreground" title={s.email}>
                                <span className="w-5 h-5 rounded-full bg-primary/15 text-primary flex items-center justify-center text-[10px] font-medium">
                                    {s.name?.[0]?.toUpperCase() || "U"}
                                </span>
                                {s.name}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </SectionCard>
    );
}

// ─────────── 10. PARTIES (Petitioner counsel BCI + opposite parties) ─────────

export function PartiesTab({ caseDoc, client, onSaved }) {
    const [petitionerBci, setPetitionerBci] = useState(caseDoc.advocate_bar_council_id || "");
    const [petitionerState, setPetitionerState] = useState(caseDoc.advocate_bar_council_state || "");
    const [opps, setOpps] = useState(
        (caseDoc.opposite_parties || []).length > 0
            ? caseDoc.opposite_parties.map(o => ({ ...o }))
            : []
    );
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        setPetitionerBci(caseDoc.advocate_bar_council_id || "");
        setPetitionerState(caseDoc.advocate_bar_council_state || "");
        setOpps((caseDoc.opposite_parties || []).map(o => ({ ...o })));
    }, [caseDoc.id, caseDoc.advocate_bar_council_id, caseDoc.advocate_bar_council_state, caseDoc.opposite_parties]);

    const setOpp = (idx, k, v) => setOpps(p => p.map((o, i) => i === idx ? { ...o, [k]: v } : o));
    const addOpp = () => setOpps(p => [...p, { name: "", advocate_name: "", advocate_bar_council_id: "" }]);
    const removeOpp = (idx) => setOpps(p => p.filter((_, i) => i !== idx));

    const save = async () => {
        const reason = window.prompt("Reason for updating parties (mandatory · logged to audit):");
        if (!reason || reason.trim().length < 3) {
            toast.error("Reason required (min 3 chars)");
            return;
        }
        setBusy(true);
        try {
            await api.patch(`/cases/${caseDoc.id}`, {
                advocate_bar_council_id: petitionerBci.trim() || null,
                advocate_bar_council_state: petitionerState.trim() || null,
                opposite_parties: opps.filter(o => (o.name || "").trim()),
            }, { headers: { "X-Change-Reason": reason } });
            toast.success("Parties saved · logged to audit");
            if (onSaved) onSaved();
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <div className="grid lg:grid-cols-2 gap-5" data-testid="case-parties-tab">
            {/* Petitioner side */}
            <SectionCard title="Petitioner / Plaintiff side" icon={Users} testId="parties-petitioner">
                <div className="space-y-3 text-sm">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Party (client)</div>
                        <div className="mt-1 text-foreground font-medium">{client?.name || "—"}</div>
                        <div className="text-xs text-muted-foreground font-mono">{client?.code || ""}</div>
                    </div>
                    <div className="pt-3 border-t border-border">
                        <label className="block">
                            <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Our counsel — Bar Council enrollment #</span>
                            <input value={petitionerBci} onChange={e => setPetitionerBci(e.target.value.toUpperCase())}
                                placeholder="K/000312/1993"
                                data-testid="parties-petitioner-bci"
                                className="mt-1 w-full bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none px-3 py-2 text-sm rounded-lg font-mono uppercase text-foreground"/>
                            <span className="text-[10px] text-muted-foreground mt-1 block">Manual entry — eCourts API does not expose this. Used on vakalath, court filings, and cause-list exports.</span>
                        </label>
                        <label className="block mt-3">
                            <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">State / Bar Council</span>
                            <input value={petitionerState} onChange={e => setPetitionerState(e.target.value)}
                                placeholder="Kerala / Bar Council of Kerala"
                                data-testid="parties-petitioner-state"
                                className="mt-1 w-full bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none px-3 py-2 text-sm rounded-lg text-foreground"/>
                        </label>
                    </div>
                </div>
            </SectionCard>

            {/* Respondent side */}
            <SectionCard title="Respondent / Opposite parties" icon={Users} testId="parties-respondent"
                action={
                    <button onClick={addOpp} data-testid="parties-add-opp"
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-border hover:border-primary rounded-lg text-xs font-medium text-foreground">
                        <Plus className="w-3 h-3"/> Add party
                    </button>
                }>
                {opps.length === 0 ? (
                    <EmptyBlock icon={Users} title="No opposite parties yet" hint="Add the respondent / defendant + their counsel and Bar Council #."/>
                ) : (
                    <div className="space-y-4">
                        {opps.map((o, idx) => (
                            <div key={idx} className="rounded-xl border border-border p-3 space-y-2" data-testid={`parties-opp-${idx}`}>
                                <div className="grid grid-cols-2 gap-2">
                                    <input value={o.name || ""} onChange={e => setOpp(idx, "name", e.target.value)}
                                        placeholder="Party name"
                                        data-testid={`parties-opp-${idx}-name`}
                                        className="bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm rounded-lg text-foreground"/>
                                    <input value={o.advocate_name || ""} onChange={e => setOpp(idx, "advocate_name", e.target.value)}
                                        placeholder="Counsel name"
                                        data-testid={`parties-opp-${idx}-advocate`}
                                        className="bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm rounded-lg text-foreground"/>
                                </div>
                                <input value={o.advocate_bar_council_id || ""} onChange={e => setOpp(idx, "advocate_bar_council_id", e.target.value.toUpperCase())}
                                    placeholder="Counsel Bar Council #   (e.g. D/001245/2001)"
                                    data-testid={`parties-opp-${idx}-bci`}
                                    className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm rounded-lg font-mono uppercase text-foreground"/>
                                <div className="flex justify-end">
                                    <button onClick={() => removeOpp(idx)}
                                        data-testid={`parties-opp-${idx}-remove`}
                                        className="text-[11px] text-rose-600 hover:underline">Remove</button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </SectionCard>

            {/* Footer action */}
            <div className="lg:col-span-2 flex justify-end">
                <button onClick={save} disabled={busy} data-testid="parties-save"
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50">
                    <Save className="w-3.5 h-3.5"/> {busy ? "Saving…" : "Save parties"}
                </button>
            </div>
        </div>
    );
}

// ─────────── AI ASSISTANT FAB + Side Panel ────────────────────────────────────

export function AiAssistantPanel({ open, onClose, caseDoc, caseId }) {
    const [busy, setBusy] = useState(false);
    const [summary, setSummary] = useState(null);

    const runSummary = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/ai/case-summary`, { case_id: caseId });
            setSummary(data.summary);
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };

    if (!open) return null;
    return (
        <>
            <div className="fixed inset-0 bg-black/40 z-40 backdrop-blur-sm" onClick={onClose} data-testid="ai-panel-backdrop"/>
            {/* Mobile: bottom-sheet sliding up. ≥sm: right-side slide-over. */}
            <aside
                className="
                    fixed z-50 bg-card flex flex-col shadow-lawos-lg
                    inset-x-0 bottom-0 top-auto max-h-[88vh] rounded-t-2xl border-t border-border
                    sm:inset-x-auto sm:top-16 sm:right-0 sm:bottom-0 sm:max-h-none sm:w-[440px] sm:rounded-none sm:border-t-0 sm:border-l sm:border-border
                "
                data-testid="case-ai-panel">
                {/* Mobile drag indicator */}
                <div className="sm:hidden flex justify-center pt-2 pb-1">
                    <div className="w-10 h-1 rounded-full bg-muted"/>
                </div>
                <div className="px-5 py-3 sm:py-4 border-b border-border flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-primary"/>
                        <h3 className="font-heading text-sm font-semibold">AI Assistant</h3>
                    </div>
                    <button onClick={onClose} data-testid="ai-panel-close" className="text-muted-foreground hover:text-foreground text-2xl leading-none px-2">×</button>
                </div>
                <div className="flex-1 overflow-y-auto p-5 space-y-4">
                    <div className="text-xs text-muted-foreground">Working on: <span className="text-foreground font-medium">{caseDoc.title}</span></div>

                    {/* Quick prompts */}
                    <div className="grid grid-cols-1 gap-2">
                        <PromptButton testId="ai-prompt-summary" busy={busy} onClick={runSummary}>
                            <Sparkles className="w-3.5 h-3.5"/> Summarise this litigation so far
                        </PromptButton>
                        <PromptButton testId="ai-prompt-draft" disabled
                            onClick={() => toast.info("Use 'Draft with AI' from Documents tab")}>
                            <FileText className="w-3.5 h-3.5"/> Draft a document (Plaint / Reply / Notice)
                        </PromptButton>
                        <PromptButton testId="ai-prompt-next-step" disabled
                            onClick={() => toast.info("Coming soon — strategic next steps")}>
                            <ArrowRight className="w-3.5 h-3.5"/> Suggest next strategic steps
                        </PromptButton>
                    </div>

                    {summary && (
                        <div className="rounded-xl bg-primary/5 border border-primary/20 p-4" data-testid="ai-panel-summary-output">
                            <div className="text-[10px] uppercase tracking-[0.2em] text-primary mb-2 font-semibold">Litigation summary</div>
                            <p className="text-sm whitespace-pre-wrap text-foreground leading-relaxed">{summary}</p>
                        </div>
                    )}

                    {!summary && !busy && (
                        <div className="text-xs text-muted-foreground italic">Pick a prompt above to start. All AI activity is metered and audited per your wallet policy.</div>
                    )}

                    {/* Smart drafts */}
                    <SmartDraftsList caseDoc={caseDoc} caseId={caseId}/>
                </div>
            </aside>
        </>
    );
}

function PromptButton({ children, busy, onClick, testId, disabled }) {
    return (
        <button onClick={onClick} disabled={busy || disabled} data-testid={testId}
            className="w-full text-left inline-flex items-center gap-2 px-3 py-2.5 rounded-lg border border-border bg-card hover:border-primary/50 hover:bg-primary/5 text-sm text-foreground disabled:opacity-50 transition-colors">
            {children}
            {busy && <span className="ml-auto text-xs text-muted-foreground">…</span>}
        </button>
    );
}

const STAGE_TO_TEMPLATES = {
    "Filed":              ["plaint", "vakalatnama"],
    "Notice issued":      ["reply", "demand_notice"],
    "Reply filed":        ["affidavit", "adjournment"],
    "Evidence":           ["affidavit"],
    "Final arguments":    ["legal_opinion"],
    "Judgment reserved":  ["legal_opinion"],
    "Pending appeal":     ["bail_application", "legal_opinion"],
    "Disposed":           [],
};
const TEMPLATE_META = {
    plaint:           { title: "Plaint",           hint: "Civil suit opening pleading" },
    reply:            { title: "Reply",            hint: "Written statement / counter" },
    affidavit:        { title: "Affidavit",        hint: "Sworn statement of facts" },
    vakalatnama:      { title: "Vakalatnama",      hint: "Memo of appearance" },
    demand_notice:    { title: "Demand notice",    hint: "Pre-litigation legal notice" },
    legal_opinion:    { title: "Legal opinion",    hint: "Strategy + applicable law" },
    bail_application: { title: "Bail application", hint: "Section-by-section grounds" },
    adjournment:      { title: "Adjournment",      hint: "Reschedule next hearing" },
};

function SmartDraftsList({ caseDoc, caseId }) {
    const stage = caseDoc?.stage || "";
    let keys = STAGE_TO_TEMPLATES[stage] || [];
    if (keys.length === 0) keys = ["adjournment", "demand_notice"];
    return (
        <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2">Smart drafts</div>
            <div className="space-y-2" data-testid="ai-panel-smart-drafts">
                {keys.map(k => {
                    const m = TEMPLATE_META[k];
                    if (!m) return null;
                    return (
                        <Link key={k} to={`/app/ai-docs?case_id=${caseId}&template=${k}`}
                            data-testid={`ai-smart-draft-${k}`}
                            className="block p-3 rounded-lg border border-border hover:border-primary/50 hover:bg-primary/5 transition-colors">
                            <div className="text-sm font-medium text-foreground">{m.title}</div>
                            <div className="text-xs text-muted-foreground mt-0.5">{m.hint}</div>
                        </Link>
                    );
                })}
            </div>
        </div>
    );
}
